import { useAuth } from "@elit-hair/elit-tools";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarSeparator,
  THEMES,
  useTheme,
} from "@elit-hair/elit-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbSettings, TbUser } from "react-icons/tb";

import { Greeting } from "@/components/greeting";
import { NavigationItems } from "@/components/sidebar/navigation-items";
import { SidebarUserButton } from "@/components/sidebar/sidebar-user-button";
import { ENV } from "@/constants/env";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { useColorSchemeChange } from "@/hooks/use-color-scheme-change";
import { useRemoteConfigInput } from "@/pages/settings/utils/use-remote-config-input";

export function SidebarLeft() {
  const { t } = useTranslation();
  const { userData, handleLogout } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const { theme, selectedColorScheme, toggleTheme, colorSchemes } = useTheme();

  const { onChange: setTheme } = useRemoteConfigInput<THEMES>(
    SETTINGS_KEYS.THEME,
  );
  const handleColorSchemeChange = useColorSchemeChange();

  // Handler to change elit-ui provider & remote config
  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    setTheme(theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
  }, [toggleTheme, setTheme]);

  const navigationItems = [
    {
      path: "/profile",
      name: t("profile"),
      icon: TbUser,
    },
    {
      path: "/settings",
      name: t("settings"),
      icon: TbSettings,
    },
  ];

  return (
    <Sidebar side="left" variant="inset" collapsible="icon">
      <SidebarHeader>Elit Panel</SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t("navigation")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <NavigationItems />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarSeparator />
          <SidebarUserButton
            userName={userData?.name || "User"}
            greeting={
              <Greeting className="text-xs leading-none text-muted-foreground" />
            }
            navigationItems={navigationItems}
            theme={theme}
            onToggleTheme={handleThemeToggle}
            colorScheme={selectedColorScheme}
            colorSchemeOptions={colorSchemes}
            onColorSchemeChange={handleColorSchemeChange}
            onLogout={() => setShowLogoutDialog(true)}
            logoutDialog={
              <AlertDialog
                open={showLogoutDialog}
                onOpenChange={setShowLogoutDialog}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("logout_confirmation")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("logout_confirmation_description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        handleLogout();
                        setShowLogoutDialog(false);
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t("logout")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            }
          />
          <SidebarSeparator />
          <p className="text-xs text-center pt-2 text-muted-foreground">
            <span className="group-data-[collapsible=icon]:hidden">
              {t("app_version")}:{" "}
            </span>
            <strong>{ENV.RELEASE_VERSION}</strong>
          </p>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
